/**
 * Where the shell keeps the instance a person is working in, on this device (round 6 T-08, owner
 * 2026-10-01: "The user's last instance should be saved and the user should login to that
 * instance").
 *
 * - **This tab** (`sessionStorage`): the instance this tab is in. A reload stays put, and two tabs
 *   in two instances never move each other. It survives the identity provider's round trip in the
 *   same tab, so signing out forgets it.
 * - **This browser** (`localStorage`): the instance this browser was last in. Used only when the
 *   person's saved instance (`GET /me` `preferences.lastInstanceId`, which follows them across
 *   browsers and devices) is not there: an older server, or a person who has never been saved.
 *   Every account on the browser shares it, so signing out forgets it too.
 *
 * The order `TenantInstanceContext` chooses in, and why, is written there. Kept apart from the
 * context so `AuthContext` can forget the choice at sign-out without importing the context that
 * imports it. Every access is guarded: a private window or blocked storage holds the choice for
 * this tab only.
 */
export const INSTANCE_STORAGE_KEY = "skydata-instance";

/** This tab's instance (round 6 T-08). */
export const TAB_INSTANCE_KEY = "skydata-instance-tab";

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    /* private mode or blocked storage: the choice holds for this tab only */
  }
}

const tab = () => sessionStorage;
const browser = () => localStorage;

/** The instance this tab was in before a reload, or null. */
export function readTabInstance(): string | null {
  return read(tab, TAB_INSTANCE_KEY);
}

/** The instance this browser was last in, or null. */
export function readBrowserInstance(): string | null {
  return read(browser, INSTANCE_STORAGE_KEY);
}

/** Records the instance this tab is now in, for this tab and for this browser. */
export function keepInstanceHere(instanceId: string): void {
  write(tab, TAB_INSTANCE_KEY, instanceId);
  write(browser, INSTANCE_STORAGE_KEY, instanceId);
}

/** Drops this tab's instance (it is gone, or the tab let go of it). */
export function forgetTabInstance(): void {
  write(tab, TAB_INSTANCE_KEY, null);
}

/** Drops this browser's instance (it is gone). */
export function forgetBrowserInstance(): void {
  write(browser, INSTANCE_STORAGE_KEY, null);
}

/**
 * Signing out: the next person to sign in here, or this person after a switch on another device,
 * lands on their own saved instance rather than on what this tab or browser last held.
 */
export function forgetInstanceChoice(): void {
  forgetTabInstance();
  forgetBrowserInstance();
}
